import type { AiCall } from "@pax/api-contract/ai/calls/v1alpha1";

/** The model a call ran, as its lab names it, or as the request named it when that's unknown. */
export const modelOf = (call: AiCall) => call.model ?? call.requested.model;

/** A moment of the call as a time, from its milliseconds after the API received it. */
function momentOf(call: AiCall, offsetMs: number | null): string | null {
  return offsetMs === null ? null : new Date(Date.parse(call.receivedAt) + offsetMs).toISOString();
}

/** The first streamed event, or the first byte for a call that didn't stream. */
export const firstChunkAt = (call: AiCall) =>
  momentOf(call, call.timing.firstEventMs ?? call.timing.firstByteMs);

/** What a seller reported it billed, or else the list cost. */
export const costOf = (call: AiCall) => call.billedCostUsd ?? call.listCostUsd;

export const cacheReadTokensOf = (call: AiCall) =>
  call.implicitCacheReadTokens + call.explicitCacheReadTokens;

/** The call ended without a whole response. */
export const failed = (call: AiCall) => call.status === "failed" || call.status === "cancelled";

/** The reply arrived but broke what the request asked for. */
export const violated = (call: AiCall) => call.status === "violated";

/** What the response reported, or what the request asked for when it reported nothing. */
export function routeFact(
  call: AiCall,
  fact: "upstream" | "tier" | "inferenceLocation" | "dataLocation",
): { value: string; reported: boolean } | null {
  const served = call.served[fact];
  if (served !== null) return { value: served, reported: true };
  const requested = call.requested[fact];
  return requested === null ? null : { value: requested, reported: false };
}

/** Who ran inference: the upstream a router reported, or the seller of a direct call. */
export const hostOf = (call: AiCall) =>
  call.billedBy === "openrouter" ? call.served.upstream : call.billedBy;
